import { useFocusEffect } from '@react-navigation/native';
import type { DependencyList } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import Constants from 'src/core/utils/Constants';
import AppStateHelper from 'src/presentation/helpers/AppStateHelper';

type Props = {
	onInit?: () => void;
	onFocussed?: () => void;
	onUnfocussed?: () => void;
	onForegrounded?: () => void;
	onBackgrounded?: () => void;
};

const depsChanged = (previous: DependencyList | null, next: DependencyList): boolean => {
	if (previous === null) {
		return true;
	}

	return previous.length !== next.length || next.some((dep, index) => !Object.is(dep, previous[index]));
};

function useAppStateScreenListener(props: Props, deps?: DependencyList) {
	const appState = useRef(AppState.currentState);
	const callbacks = useRef(props);
	const initializedVersion = useRef<number | null>(null);

	useEffect(() => {
		callbacks.current = props;
	});

	const currentDeps = deps ?? [];
	const [depsState, setDepsState] = useState<{ version: number; deps: DependencyList | null }>({
		version: 0,
		deps: null,
	});

	if (depsChanged(depsState.deps, currentDeps)) {
		setDepsState({ version: depsState.version + 1, deps: currentDeps });
	}

	const depsVersion = depsState.version;

	useFocusEffect(
		useCallback(() => {
			const appStateChangedListener = AppState.addEventListener(Constants.appStateEventChange, (nextAppState) => {
				if (
					AppStateHelper.isForegrounded({
						current: appState.current,
						next: nextAppState,
					})
				) {
					callbacks.current.onForegrounded?.();
				} else {
					callbacks.current.onBackgrounded?.();
				}

				appState.current = nextAppState;
			});

			if (initializedVersion.current !== depsVersion) {
				initializedVersion.current = depsVersion;
				callbacks.current.onInit?.();
			}

			callbacks.current.onFocussed?.();

			return () => {
				callbacks.current.onUnfocussed?.();
				appStateChangedListener.remove();
			};
		}, [depsVersion]),
	);

	return null;
}

export default useAppStateScreenListener;
